import innerEmitter from '../inner-emitter/inner-emitter';
import { INNER_EVENT } from '../inner-emitter/inner-type';
import kvStatManager, { KV_REPORT_KEY_AUDIO_CONTEXT } from '../manager/kv-stat-manager';
import { IS_IOS } from './environment';
import loggerManager from './log/logger-manager';
import { performanceNow } from './utils';

let AudioContextClass: typeof AudioContext;

if (typeof AudioContext !== 'undefined') AudioContextClass = AudioContext;
// @ts-ignore
else if (typeof webkitAudioContext !== 'undefined') AudioContextClass = webkitAudioContext;
// @ts-ignore
else if (typeof mozAudioContext !== 'undefined') AudioContextClass = mozAudioContext;

export const LONG_SUSPENDED_THRESHOLD = 1500;

let context: AudioContext;
let retryTimeoutId = -1;
let lastSuspendedTime = 0;
let checkLongTimeSuspendedId = -1;
let isLongTimeSuspended = false;
let lastCheckFakeRunningTime = 0;
let lastCurrentTime = -1;
let fakeRunningDetectId = -1;

initAudioContext();

function initAudioContext() {
  try {
    if (context) return;
    context = new AudioContextClass({ sampleRate: 48000 });
    context.onstatechange = () => {
      loggerManager.info(`context state: ${context.state}${context.state !== 'running' ? ` visibilityState: ${document.visibilityState}` : ''}`);
      resumeWhenSuspended();
    };
    clearTimeout(retryTimeoutId);
  } catch (error) {
    loggerManager.error(`initAudioContext failed: ${error} typeof AudioContextClass: ${typeof AudioContextClass}`);
    // iOS wkwebview 中，AudioContext 初始化会报错，需要延迟 1s 后重试
    // ref: https://tapd.woa.com/tapd_fe/20396022/story/detail/1020396022121321250
    retryTimeoutId = setTimeout(initAudioContext, 1000);
  }
}
const resumeWhenSuspended = () => {
  // 可能因自动播放限制导致 context 处于 suspended 状态，此时监听页面点击事件恢复。
  if (context.state === 'suspended') {
    lastSuspendedTime = performanceNow();
    startLongSuspendedDetect();
    resumeContext();
    document.addEventListener('click', resumeWhenSuspended);
    // @ts-ignore
  } else if (context.state === 'interrupted') {
    resumeContext();
  } else {
    if (lastSuspendedTime) {
      // v5.12.1+ 增加了 1500 ~ 2000 的统计
      kvStatManager.addNumber({ key: KV_REPORT_KEY_AUDIO_CONTEXT.SUSPENDED_DURATION, value: performanceNow() - lastSuspendedTime, split: [0, 500, 1000, 1500, 2000, 3000, 4000, 5000, 10000, 30000], max: 60000 });
      lastSuspendedTime = 0;
    }
    clearLongSuspendedDetect();
    document.removeEventListener('visibilitychange', resumeWhenSuspended);
    document.removeEventListener('click', resumeWhenSuspended);
  }
};

let lastResumeTime = 0;
let resumeTimeoutId = -1;
export function resumeContext(): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    if (context.state === 'running') return resolve();
    // 防止频繁调用 resume
    if (Date.now() - lastResumeTime < 1000) {
      clearTimeout(resumeTimeoutId);
      resumeTimeoutId = setTimeout(() => {
        lastResumeTime = Date.now();
        context.resume().then(resolve, reject)
          .finally(() => {
            startFakeRunningDetect();
          });
      }, 1000);
    } else {
      clearTimeout(resumeTimeoutId);
      lastResumeTime = Date.now();
      context.resume().then(resolve, reject)
        .finally(() => {
          startFakeRunningDetect();
        });
    }
  }).catch(error => {
    loggerManager.warn(`context resume failed: ${error}`);
    document.addEventListener('visibilitychange', resumeWhenSuspended);
  });
}
document.addEventListener('click', resumeWhenSuspended);

function startLongSuspendedDetect() {
  if (checkLongTimeSuspendedId !== -1) return;
  checkLongTimeSuspendedId = setTimeout(() => {
    if (context.state === 'suspended') {
      isLongTimeSuspended = true;
      innerEmitter.emit(INNER_EVENT.AUDIO_CONTEXT_LONG_SUSPENDED, { isSuspended: true });
    }
  }, LONG_SUSPENDED_THRESHOLD);
}

function clearLongSuspendedDetect() {
  if (checkLongTimeSuspendedId === -1) return;
  clearTimeout(checkLongTimeSuspendedId);
  checkLongTimeSuspendedId = -1;
  if (isLongTimeSuspended) {
    isLongTimeSuspended = false;
    innerEmitter.emit(INNER_EVENT.AUDIO_CONTEXT_LONG_SUSPENDED, { isSuspended: false });
  }
}

// iOS 使用 audioContext 时, 切换别的 app 播放声音, 会导致网页无声
// 此时 context 状态是 running, 且 currentTime 不变
export function startFakeRunningDetect() {
  if (!IS_IOS) return;
  if (fakeRunningDetectId !== -1) return;
  const _checkFakeRunning = () => {
    if (performanceNow() - lastCheckFakeRunningTime < 500) return;
    if (context && context.state === 'running' && context.currentTime === lastCurrentTime) {
      loggerManager.warn('context is fake running, auto resume');
      context.suspend().catch(error => {
        loggerManager.warn(`context suspend failed: ${error}`);
      });
    }
    lastCurrentTime = context.currentTime;
    lastCheckFakeRunningTime = performanceNow();
  };
  fakeRunningDetectId = setInterval(() => {
    _checkFakeRunning();
  }, 2000);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      _checkFakeRunning();
    }
  });
}

export const isAudioContextRunning = () => context.state === 'running';
export const getAudioContext = (label?: 'music' | 'volume-meter' | 'audio-mixer' | 'denoiser' | 'player' | 'dump') => context;
